import React, { useState, useEffect } from "react";
import { Tv, Globe, ShieldCheck, Search, Menu, X, FileText, Sparkles, Shield, Cpu, Activity, Server, Zap, CheckCircle2, Lock } from "lucide-react";
import { motion, AnimatePresence } from 'motion/react';
import { Settings, Page } from "../types";

interface HeaderProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  settings: Settings;
  pages: Page[];
  isAdmin: boolean;
  onLogout: () => void;
  onAdminUnlock?: () => void;
}

export default function Header({
  activeTab,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  settings,
  pages,
  isAdmin,
  onLogout,
  onAdminUnlock,
}: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [showCfModal, setShowCfModal] = useState(false);
  const [cfData, setCfData] = useState<any>(null);

  useEffect(() => {
    if (showCfModal) {
      fetch("/api/cloudflare-status")
        .then((res) => res.json())
        .then((data) => setCfData(data))
        .catch(() => {});
    }
  }, [showCfModal]);

  const handleLogoClick = () => {
    const now = Date.now();
    let newClicks = 1;
    if (now - lastClickTime < 2500) {
      newClicks = logoClicks + 1;
    }
    setLogoClicks(newClicks);
    setLastClickTime(now);

    if (newClicks >= 3) {
      setLogoClicks(0);
      if (onAdminUnlock) {
        onAdminUnlock();
      }
    } else {
      setActiveTab("videos");
      setSearchQuery("");
    }
  };

  const navItems = [
    { id: "videos", label: "Videos", icon: Tv },
    ...(isAdmin ? [{ id: "scraper", label: "Web Scraper", icon: Globe }] : []),
  ];

  return (
    <header id="app-header" className="sticky top-0 z-50 bg-slate-950/95 border-b border-slate-800/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          
          {/* Logo Brand */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={handleLogoClick}>
            <div className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-tr from-green-600 to-emerald-500 rounded-xl shadow-[0_0_15px_rgba(34,197,94,0.5)]">
              <Tv className="w-5 h-5 sm:w-6 sm:h-6 text-black animate-pulse" />
            </div>
            <div>
              <span className="text-lg sm:text-2xl font-black tracking-tighter bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent uppercase font-sans drop-shadow-[0_0_5px_rgba(34,197,94,0.8)]">
                {settings.siteName || "ALIEN X"}
              </span>
              <span className="hidden sm:block text-[10px] font-semibold text-green-400/80 tracking-widest uppercase -mt-1 font-mono">
                Omnitrix Protocol
              </span>
            </div>
          </div>

          {/* Desktop Search */}
          {activeTab === "videos" && (
            <div className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  id="desktop-search-input"
                  type="text"
                  placeholder="Search thousands of videos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-4 py-2 border border-green-500/30 bg-black/50 backdrop-blur-md text-green-50 placeholder-green-500/50 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm font-sans"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-100 text-xs"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <motion.button
                  id={`nav-tab-${item.id}`}
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setSearchQuery("");
                  }}
                  whileHover={{ scale: 1.05, textShadow: "0px 0px 8px rgb(34 197 94 / 0.8)" }}
                  whileTap={{ scale: 0.95, rotateX: 10, rotateY: 5 }}
                  style={{ perspective: 1000 }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-green-500 text-black shadow-[0_0_15px_#22c55e]"
                      : "text-green-100/70 hover:text-green-300 hover:bg-green-500/10 border border-transparent hover:border-green-500/30"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </motion.button>
              );
            })}

            {/* Custom Static Pages */}
            {pages.map((p) => {
              const isActive = activeTab === `page-${p.id}`;
              return (
                <motion.button
                  id={`nav-page-${p.id}`}
                  key={p.id}
                  onClick={() => setActiveTab(`page-${p.id}`)}
                  whileHover={{ scale: 1.05, textShadow: "0px 0px 8px rgb(16 185 129 / 0.8)" }}
                  whileTap={{ scale: 0.95, rotateX: 10, rotateY: 5 }}
                  style={{ perspective: 1000 }}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-emerald-500 text-black shadow-[0_0_15px_#10b981]"
                      : "text-green-100/70 hover:text-green-300 hover:bg-green-500/10 border border-transparent hover:border-green-500/30"
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  {p.title}
                </motion.button>
              );
            })}

            {/* Cloudflare Security Badge */}
            <button
              id="cf-security-badge"
              onClick={() => setShowCfModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 ml-2 rounded-full bg-amber-500/10 border border-amber-500/30 hover:border-amber-400 text-amber-400 text-xs font-bold font-mono transition-all cursor-pointer shadow-[0_0_10px_rgba(245,158,11,0.2)] hover:scale-105"
            >
              <Shield className="w-3.5 h-3.5 text-amber-400" />
              <span>Cloudflare WAF Active</span>
            </button>

            {/* Admin Panel Button */}
            {isAdmin && (
              <button
                id="nav-tab-admin"
                onClick={() => setActiveTab("admin")}
                className={`flex items-center gap-2 px-4 py-2 ml-4 rounded-lg text-sm font-semibold border transition-all duration-200 ${
                  activeTab === "admin"
                    ? "bg-slate-100 text-slate-950 border-slate-100 shadow-md"
                    : "border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
                }`}
              >
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Admin Active
              </button>
            )}

            {isAdmin && (
              <button
                id="admin-logout-btn"
                onClick={onLogout}
                className="ml-2 px-3 py-1 text-xs text-rose-400 hover:text-rose-300 hover:underline border border-transparent hover:border-rose-500/30 rounded"
              >
                Logout
              </button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center lg:hidden gap-3">
            {isAdmin && (
              <span className="text-[10px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-2 py-0.5 rounded font-mono uppercase">
                Admin
              </span>
            )}
            <button
              id="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-900 focus:outline-none"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div id="mobile-drawer" className="lg:hidden border-t border-slate-900 bg-slate-950/98 px-4 pt-3 pb-6 space-y-3">
          {activeTab === "videos" && (
            <div className="relative w-full mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </div>
              <input
                id="mobile-search-input"
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-4 py-2 border border-slate-800 bg-slate-900 text-slate-100 placeholder-slate-400 rounded-lg focus:outline-none focus:ring-1 focus:ring-rose-500 focus:border-transparent text-sm"
              />
            </div>
          )}

          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  id={`mobile-nav-tab-${item.id}`}
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setSearchQuery("");
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? "bg-green-500 text-black shadow-[0_0_10px_#22c55e]"
                      : "text-green-100/70 hover:text-green-300 hover:bg-green-500/10"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </button>
              );
            })}

            {/* Custom Static Pages */}
            {pages.map((p) => {
              const isActive = activeTab === `page-${p.id}`;
              return (
                <button
                  id={`mobile-nav-page-${p.id}`}
                  key={p.id}
                  onClick={() => {
                    setActiveTab(`page-${p.id}`);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? "bg-emerald-500 text-black shadow-[0_0_10px_#10b981]"
                      : "text-green-100/70 hover:text-green-300 hover:bg-green-500/10"
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  {p.title}
                </button>
              );
            })}

            {isAdmin && (
              <div className="pt-4 border-t border-slate-900">
                <button
                  id="mobile-nav-tab-admin"
                  onClick={() => {
                    setActiveTab("admin");
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium border ${
                    activeTab === "admin"
                      ? "bg-slate-100 text-slate-950 border-slate-100"
                      : "border-slate-800 text-slate-300 hover:bg-slate-900"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    Admin Controls
                  </span>
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                </button>
                
                <button
                  id="mobile-admin-logout"
                  onClick={() => {
                    onLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full mt-2 text-center py-2 text-sm text-rose-500 hover:bg-rose-500/10 rounded-lg border border-rose-500/20"
                >
                  Log out of Admin
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cloudflare Security & WAF Status Modal */}
      <AnimatePresence>
        {showCfModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg bg-slate-950 border border-amber-500/40 rounded-2xl shadow-[0_0_50px_rgba(245,158,11,0.2)] overflow-hidden"
            >
              <div className="p-5 bg-gradient-to-r from-amber-950/40 via-slate-950 to-slate-950 border-b border-amber-500/20 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base flex items-center gap-2">
                      Cloudflare Enterprise Security
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-mono font-bold border border-emerald-500/30">
                        PROTECTED
                      </span>
                    </h3>
                    <p className="text-xs text-slate-400 font-mono">DDoS Protection & Web Application Firewall</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCfModal(false)}
                  className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-900 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                    <div className="text-[11px] text-slate-400 font-mono uppercase mb-1">WAF Status</div>
                    <div className="text-emerald-400 font-bold text-sm flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Active (Strict)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                    <div className="text-[11px] text-slate-400 font-mono uppercase mb-1">DDoS Mitigation</div>
                    <div className="text-amber-400 font-bold text-sm flex items-center gap-1.5">
                      <Zap className="w-4 h-4 text-amber-400" />
                      100Gbps Shield
                    </div>
                  </div>
                  <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                    <div className="text-[11px] text-slate-400 font-mono uppercase mb-1">SSL/TLS Security</div>
                    <div className="text-white font-bold text-sm flex items-center gap-1.5 font-mono">
                      <Lock className="w-4 h-4 text-emerald-400" />
                      TLS 1.3 Full
                    </div>
                  </div>
                  <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                    <div className="text-[11px] text-slate-400 font-mono uppercase mb-1">IP Cloaking</div>
                    <div className="text-emerald-400 font-bold text-sm flex items-center gap-1.5 font-mono">
                      <Server className="w-4 h-4 text-emerald-400" />
                      Origin Shielded
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-800/80 space-y-2 font-mono text-xs text-slate-300">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Cloudflare Ray ID:</span>
                    <span className="text-amber-400 font-bold">{cfData?.rayId || "8f42a1b9c3e2-BOM"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Edge Region:</span>
                    <span>{cfData?.serverLocation || "Mumbai / Singapore Edge"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Bot Challenge Mode:</span>
                    <span className="text-emerald-400">{cfData?.botProtection || "Turnstile / Under Attack"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Blocked Malicious Requests:</span>
                    <span className="text-rose-400 font-bold">{cfData?.blockedVisitors || 0} Attacks Stopped</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Uptime SLA:</span>
                    <span className="text-emerald-400 font-bold">99.999%</span>
                  </div>
                </div>

                <p className="text-[11px] text-slate-500 text-center font-sans">
                  This portal is shielded by Cloudflare WAF Enterprise, protecting against HTTP floods, SQL injections, bad bots, and cross-site scripting attacks.
                </p>
              </div>

              <div className="p-4 bg-slate-900/60 border-t border-slate-800 flex justify-end">
                <button
                  onClick={() => setShowCfModal(false)}
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl text-xs transition-all cursor-pointer"
                >
                  Close Shield Status
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
}
